import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { clearApiCache } from '../lib/apiCache';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Use sessionStorage for tokens (cleared on tab close, more secure than localStorage)
const tokenStorage = {
  get: () => sessionStorage.getItem('token') || localStorage.getItem('token'),
  set: (val) => { sessionStorage.setItem('token', val); localStorage.setItem('token', val); },
  remove: () => { sessionStorage.removeItem('token'); localStorage.removeItem('token'); },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(tokenStorage.get());
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const userData = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
      setUser(userData);

      // If user data is stale (missing available_features), refresh from server
      if (userData && !userData.available_features) {
        axios.post(`${API_URL}/auth/refresh`, {}).then(res => {
          const refreshedUser = res.data.user;
          sessionStorage.setItem('user', JSON.stringify(refreshedUser));
          localStorage.setItem('user', JSON.stringify(refreshedUser));
          if (res.data.token) {
            tokenStorage.set(res.data.token);
            setToken(res.data.token);
          }
          setUser(refreshedUser);
        }).catch(() => {
          // If refresh fails, keep old data — fallback in Layout handles it
        });
      }
    }
    setLoading(false);
  }, [token]);

  // Global 401 interceptor — session expired
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && token) {
          // Token expired or invalid — auto logout
          tokenStorage.remove();
          sessionStorage.removeItem('user');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
          setSessionExpired(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;

    tokenStorage.set(newToken);
    sessionStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.setItem('login_time', new Date().toISOString());
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    // Sync theme from server profile
    if (userData.theme) {
      localStorage.setItem('theme', userData.theme);
      window.dispatchEvent(new Event('theme-changed'));
    }

    return userData;
  };

  const logout = useCallback(() => {
    tokenStorage.remove();
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('login_time');
    localStorage.removeItem('user');
    localStorage.removeItem('login_time');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    clearApiCache();
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      sessionStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, updateUser, token, login, logout, loading, sessionExpired, setSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
